import {auth} from "../firebase";
import { beginBackendRequest, composeBackendAbortSignals } from './backendRequestSession';
import { FACTOR_CONTEXT_CHANGED_EVENT, isFactorContextChangedError } from './factorContextChange';

export const CUSTOMER_ACCESS_DENIED_EVENT = 'alphamizer:customer-access-denied';

const configuredBackendUrl = (
    typeof __ALPHAMIZER_BACKEND_URL__ === 'undefined'
        ? ''
        : __ALPHAMIZER_BACKEND_URL__
);

export class BackendHttpError extends Error {
    constructor(status, errorText, code = null, detail = null) {
        const explanation = detail?.message || (status >= 500
            ? 'The service could not complete this request. Please try again.'
            : `Cloud Run call failed: ${status} - ${errorText}`);
        super(`${explanation}${detail?.reference ? ` Reference: ${detail.reference}.` : ''}`);
        this.name = 'BackendHttpError';
        this.status = status;
        this.code = code;
        this.detail = detail?.detail ?? null;
        this.reference = detail?.reference ?? null;
        this.retryable = detail?.retryable ?? null;
    }
}

function backendErrorDetail(errorText) {
    try {
        const parsed = JSON.parse(errorText);
        const detail = parsed?.detail;
        const code = parsed?.code ?? parsed?.error_code ?? detail?.code;
        const message = parsed?.message ?? detail?.message ?? (typeof detail === 'string' ? detail : null);
        const reference = parsed?.reference ?? detail?.reference;
        const retryable = parsed?.retryable ?? detail?.retryable;
        return {
            code: typeof code === 'string' && code.trim() ? code.trim() : null,
            message: typeof message === 'string' && message.trim() ? message.trim() : null,
            reference: typeof reference === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(reference) ? reference : null,
            retryable: typeof retryable === 'boolean' ? retryable : null,
            detail: detail ?? parsed,
        };
    } catch {
        return { code: null, message: null };
    }
}

const CUSTOMER_ACCESS_DENIAL_CODES = new Set([
    'USER_NOT_PROVISIONED',
    'USER_INACTIVE',
    'ACCESS_NOT_STARTED',
    'ACCESS_EXPIRED',
    'ACCESS_SUSPENDED',
    'ACCESS_CONFIGURATION_INVALID',
]);

export function isCustomerAccessDeniedError(error) {
    return error instanceof BackendHttpError
        && error.status === 403
        && CUSTOMER_ACCESS_DENIAL_CODES.has(error.code);
}

function publishCustomerAccessDenial(error, message = null) {
    if (typeof window === 'undefined' || !isCustomerAccessDeniedError(error)) {
        return;
    }
    window.dispatchEvent(new CustomEvent(CUSTOMER_ACCESS_DENIED_EVENT, {
        detail: { code: error.code, message },
    }));
}

function publishFactorContextChange(error) {
    if (typeof window === 'undefined' || !isFactorContextChangedError(error)) {
        return;
    }
    window.dispatchEvent(new CustomEvent(FACTOR_CONTEXT_CHANGED_EVENT));
}

export async function throwBackendHttpError(response) {
    const errorText = await response.text().catch(() => '');
    const detail = backendErrorDetail(errorText);
    const error = new BackendHttpError(response.status, errorText, detail.code, detail);
    publishCustomerAccessDenial(error, detail.message);
    publishFactorContextChange(error);
    throw error;
}

export function isUserNotProvisionedError(error) {
    return (
        error instanceof BackendHttpError
        && error.status === 404
        && error.code === 'USER_NOT_PROVISIONED'
    );
}

const INVITATION_DENIAL_CODES = new Set([
    'INVITATION_NOT_AVAILABLE',
    'INVITATION_EXPIRED',
    'INVITATION_EMAIL_UNVERIFIED',
    'INVITATION_PROVIDER_UNSUPPORTED',
    'INVITATION_IDENTITY_CONFLICT',
]);

export function isInvitationDenialError(error) {
    return error instanceof BackendHttpError && INVITATION_DENIAL_CODES.has(error.code);
}

export function getBackendBaseUrl() {
    return String(configuredBackendUrl || '').replace(/\/+$/, '');
}

export async function callBackend(endpoint, method = 'POST', data = null, options = {}) {
    const request = beginBackendRequest();
    const combinedAbort = composeBackendAbortSignals(request.signal, options?.signal);

    try {
        const baseUrl = getBackendBaseUrl();
        const url = `${baseUrl}/${endpoint}`;
        const fetchOptions = {
            method,
            headers: {'Content-Type': 'application/json'},
            // The bearer token below is the only credential; the backend uses no cookies.
            credentials: 'omit',
            signal: combinedAbort.signal,
        };

        const token = await auth.currentUser?.getIdToken();
        if (token) {
            fetchOptions.headers.Authorization = `Bearer ${token}`;
        }

        if (data) {
            fetchOptions.body = JSON.stringify(data);
        }

        let response;
        try {
            response = await fetch(url, fetchOptions);
        } catch (error) {
            // Preserve Error identity; parser/programming errors are not fetch failures.
            if (error instanceof TypeError && !combinedAbort.signal.aborted) {
                try { Object.defineProperty(error, 'backendTransportFailure', { value: true }); } catch (_error) {
                    // A frozen foreign Error remains a strict, unclassified failure.
                }
            }
            throw error;
        }
        if (!response.ok) {
            await throwBackendHttpError(response);
        }

        return response.json();
    } finally {
        combinedAbort.cleanup();
        request.finish();
    }
}

export async function callBackendBlob(endpoint, method = 'GET', data = null) {
    const request = beginBackendRequest();

    try {
        const baseUrl = getBackendBaseUrl();
        const url = `${baseUrl}/${endpoint}`;
        const fetchOptions = {
            method,
            headers: {},
            credentials: 'omit',
            signal: request.signal,
        };

        const token = await auth.currentUser?.getIdToken();
        if (token) {
            fetchOptions.headers.Authorization = `Bearer ${token}`;
        }

        if (data) {
            fetchOptions.headers['Content-Type'] = 'application/json';
            fetchOptions.body = JSON.stringify(data);
        }

        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
            await throwBackendHttpError(response);
        }

        return {
            blob: await response.blob(),
            filename: filenameFromContentDisposition(
                response.headers?.get?.('Content-Disposition'),
            ),
        };
    } finally {
        request.finish();
    }
}

function filenameFromContentDisposition(value) {
    const header = value == null ? '' : String(value);
    const match = header.match(/filename="?([^";]+)"?/i);
    return match ? match[1] : null;
}
